import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Dashboard from "@/pages/Dashboard";
import Players from "@/pages/Players";
import Duel from "@/pages/Duel";
import Gallery from "@/pages/Gallery";
import PlayerDetail from "@/pages/PlayerDetail";
import Report from "@/pages/Report";
import PublicShare from "@/pages/PublicShare";
import ClubHome from "@/pages/ClubHome";
import Invite from "@/pages/Invite";
import Audit from "@/pages/Audit";
import Manage from "@/pages/Manage";
import Matches from "@/pages/Matches";
import MatchDetail from "@/pages/MatchDetail";
import Officials from "@/pages/Officials";
import Gala from "@/pages/Gala";
import Payments from "@/pages/Payments";
import PaymentsAdmin from "@/pages/PaymentsAdmin";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PaymentCancel from "@/pages/PaymentCancel";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Profile from "@/pages/Profile";
import Install from "@/pages/Install";
import Leaderboard from "@/pages/Leaderboard";
import History from "@/pages/History";
import Settings from "@/pages/Settings";
import PendingApproval from "@/pages/PendingApproval";
import PendingPlayers from "@/pages/PendingPlayers";
import InstallPWA from "@/components/InstallPWA";
import PushBanner from "@/components/PushBanner";
import UpdateBanner from "@/components/UpdateBanner";

function Root() {
  const { user } = useAuth();
  if (user === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0B0F17]">
        <div className="text-slate-400 font-mono-stat text-sm uppercase tracking-widest">
          Chargement...
        </div>
      </div>
    );
  }
  return <Navigate to={user ? "/dashboard" : "/club"} replace />;
}

function AuthedExtras() {
  const { user } = useAuth();
  if (!user) return null;
  return <PushBanner />;
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster position="top-right" theme="dark" richColors />
        <UpdateBanner />
        <InstallPWA />
        <AuthedExtras />
        <Routes>
          <Route path="/" element={<Root />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/install" element={<Install />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route
            path="/profile"
            element={
              <ProtectedRoute>
                <Layout>
                  <Profile />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <Layout>
                  <Dashboard />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/players"
            element={
              <ProtectedRoute>
                <Layout>
                  <Players />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/players/:id"
            element={
              <ProtectedRoute>
                <Layout>
                  <PlayerDetail />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route path="/public/:token" element={<PublicShare />} />
          <Route path="/club" element={<ClubHome />} />
          <Route path="/invite/:token" element={<Invite />} />
          <Route
            path="/report"
            element={
              <ProtectedRoute>
                <Layout>
                  <Report />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/gallery"
            element={
              <ProtectedRoute>
                <Layout>
                  <Gallery />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/teams"
            element={<Navigate to="/players" replace />}
          />
          <Route
            path="/matches"
            element={
              <ProtectedRoute>
                <Layout>
                  <Matches />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/matches/:id"
            element={
              <ProtectedRoute>
                <Layout>
                  <MatchDetail />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/officiels"
            element={
              <ProtectedRoute>
                <Layout>
                  <Officials />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/gala"
            element={
              <ProtectedRoute>
                <Layout>
                  <Gala />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route path="/paiement/success" element={<PaymentSuccess />} />
          <Route path="/paiement/cancel" element={<PaymentCancel />} />
          <Route
            path="/paiement"
            element={
              <ProtectedRoute>
                <Layout>
                  <Payments />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/manage/paiements"
            element={
              <ProtectedRoute coachOnly>
                <Layout>
                  <PaymentsAdmin />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/leaderboard"
            element={
              <ProtectedRoute>
                <Layout>
                  <Leaderboard />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/duel"
            element={
              <ProtectedRoute>
                <Layout>
                  <Duel />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/history"
            element={
              <ProtectedRoute>
                <Layout>
                  <History />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/manage"
            element={
              <ProtectedRoute>
                <Layout>
                  <Manage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/manage/pending"
            element={
              <ProtectedRoute coachOnly>
                <Layout>
                  <PendingPlayers />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route path="/pending-approval" element={<PendingApproval />} />
          <Route
            path="/audit"
            element={
              <ProtectedRoute>
                <Layout>
                  <Audit />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings"
            element={
              <ProtectedRoute coachOnly>
                <Layout>
                  <Settings />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
